import { type Control, Controller, type UseFormReturn } from 'react-hook-form';
import type { songSchemas } from '@/api/songs/song.schema';
import { Button } from '@/components/ui/button';
import { Form, FormContent } from '@/components/ui/form';
import { FormAsyncSelect } from '@/components/ui/form-fields/form-async-select';
import { FormInput } from '@/components/ui/form-fields/form-input';
import { FormNumberInput } from '@/components/ui/form-fields/form-number-input';
import { FormSelect } from '@/components/ui/form-fields/form-select';
import { FormSwitch } from '@/components/ui/form-fields/form-switch';
import { Icon } from '@/components/ui/icon';
import { useToast } from '@/hooks/use-toast';
import { SONG_KEYS } from '@/lib/song';
import {
  type ArtistOption,
  createArtistOptionsQuery,
  useCreateArtist,
} from '@/services/artists';
import { SongContentEditor } from './song-content-editor';

export type SongFormSchema = import('zod').input<typeof songSchemas.create>;

const keyOptions = SONG_KEYS.map((key) => ({ label: key, value: key }));

type Props = {
  form: UseFormReturn<SongFormSchema>;
  isSubmitting: boolean;
  onSubmit: (data: SongFormSchema) => void;
  onCancel: () => void;
};

/** Os dois cards do editor de cifra (dados + cifra) e o rodapé; usado em criar e editar. */
export const SongForm: React.FC<Props> = ({ form, isSubmitting, onSubmit, onCancel }) => {
  const artistId = form.watch('artistId');
  const { mutateAsync: createArtist } = useCreateArtist();
  const toast = useToast();

  /** Cadastra o artista só com o nome, direto do select (já sai selecionado). */
  const handleCreateArtist = async (name: string) => {
    try {
      const artist = await createArtist({ name });
      return artist ? { id: artist.id, name: artist.name } : null;
    } catch (error: any) {
      toast.error(error?.value?.errors?.name ?? 'Não foi possível criar o artista.');
      return null;
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <Form
        id="form"
        title="Dados da cifra"
        description="Inativa continua na listagem, mas não pode entrar em repertórios novos."
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <FormContent>
          <FormInput name="title" control={form.control} label="Nome" required />
          <FormAsyncSelect<SongFormSchema, ArtistOption>
            name="artistId"
            control={form.control as Control<SongFormSchema>}
            label="Artista"
            useQuery={createArtistOptionsQuery(artistId)}
            getOptionValue={(option) => option.id}
            getDisplayValue={(option) => option.name}
            onCreate={handleCreateArtist}
            createLabel={(name) => `Criar artista "${name}"`}
          />
          <FormSelect
            name="originalKey"
            control={form.control}
            label="Tom original"
            required
            options={keyOptions}
          />
          <FormNumberInput
            name="capo"
            control={form.control}
            label="Capo (casa)"
            min={0}
            max={12}
            decimalScale={0}
          />
          <FormInput name="notes" control={form.control} label="Observações" />
          <FormSwitch name="isActive" control={form.control} label="Ativa" />
        </FormContent>
      </Form>

      <Controller
        name="content"
        control={form.control}
        render={({ field, fieldState }) => (
          <SongContentEditor
            value={field.value ?? ''}
            onChange={field.onChange}
            error={fieldState.error?.message}
          />
        )}
      />

      <div className="flex items-center justify-end gap-3">
        <Button
          type="button"
          variant="outline"
          disabled={isSubmitting}
          onClick={onCancel}
        >
          Cancelar
        </Button>
        <Button
          type="submit"
          form="form"
          variant="success"
          disabled={isSubmitting}
          className="flex items-center gap-2"
        >
          <Icon name="save" />
          Salvar cifra
        </Button>
      </div>
    </div>
  );
};
